import { type DragEvent, useCallback, useState } from 'react';
import { type Edge, type Node, type ReactFlowInstance } from '@xyflow/react';
import * as Api from '@ViewModels';
import { toast } from 'sonner';
import type { BaseNodeData, FlowContact } from '../types';
import { type BranchEdgeData, EDGE_TYPES, type PhantomAddPathEdgeData } from '../types/edges';
import { deriveParentCaseInfoForNewNode } from '../utils/branchParentCase';
import { NODE_TYPES } from '../types';
import { canAddStepAfterNode, type ChainTerminus } from './useChainTermini';
import type { HistoryActionType } from './useUndoRedo';
import { addNodeWithLayout, findClosestTerminus, insertNodeAfter } from '../utils/autoLayout';
import { applySwitchBranchLayout } from '../utils/layoutEngine';
import {
	BRANCH_MARGIN,
	BRANCH_VERTICAL_GAP,
	FLOW_VERTICAL_SPACING,
	NODE_WIDTH,
	SEGMENT_NODE,
	STEP_NODE_COMPACT_HEIGHT,
	STEP_NODE_EXPANDED_MIN_HEIGHT,
} from '../utils/layoutConstants';
import { hasEstimatedHeight } from '../types/typeGuards';
import { reconcileOptimisticStepNode } from '../utils/reconcileOptimisticStepNode';
import {
	buildPersistedSwitchCaseAssignments,
	syncOptimisticSwitchEdges,
	syncOptimisticSwitchNodes,
} from '../utils/optimisticSwitchSync';
import { buildTemplateForNode, resolveTargetTemplateIdForTerminus } from '../utils/templateResolution';
import { scheduleCenterOnNode } from '../utils/viewport';
import { wouldCreateCycle } from '../utils/layoutHelpers';
import {
	createSegmentNode,
	createSwitchStepNode,
	createTriggerNode,
	createStepNodeByType,
	generateNodeId,
} from '../utils/nodeFactory';
import { MAX_BRANCH_NESTING_DEPTH } from '../utils/nestingLimits';
import {
	canUseTriggerAutomationStep,
	isTriggerAutomationStepNode,
	TRIGGER_AUTOMATION_LAST_STEP_TOAST,
	TRIGGER_AUTOMATION_TRIGGER_TYPE_TOAST,
} from '../utils/triggerAutomationStepRules';
import { normalizeBehaviorBranchSchedule } from '../utils/behaviorBranchSchedule';

interface SwitchStepInsertResult {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	focusNodeId: string;
	switchNodeId: string;
}

/**
 * Builds a hidden switch step with a blank case + default case rendered as segment nodes.
 */
export function addSwitchStepWithSegments(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	position: { x: number; y: number },
	parentNodeId?: string,
	automationTemplate?: Api.IAutomationTemplate,
	rootTrigger?: Api.IAutomationTrigger | null
): SwitchStepInsertResult {
	const blankCaseId = generateNodeId('case');
	const defaultCaseId = generateNodeId('case');

	const blankCase = {
		id: blankCaseId,
		isDefault: false,
	} as Api.IAutomationStepCaseStatement;

	const defaultCase = {
		id: defaultCaseId,
		isDefault: true,
		filter: null,
	} as Api.IAutomationStepCaseStatement;

	const switchStep = {
		_type: 'SwitchAutomationStep',
		cases: [blankCase, defaultCase],
	} as Api.ISwitchAutomationStep;

	const switchNode = createSwitchStepNode(position, switchStep, automationTemplate);
	switchNode.hidden = true;
	switchNode.data.summary = '2 branches';
	switchNode.data.outputHandles = ['default'];

	const segmentNodes = [
		createSegmentNode(blankCase, 0, switchNode.id, position),
		createSegmentNode(defaultCase, 1, switchNode.id, position),
	];

	const visualSourceNodeId = parentNodeId ?? switchNode.id;
	const totalCases = segmentNodes.length;

	const branchEdges: Edge<BranchEdgeData>[] = segmentNodes.map((segment, index) => {
		const caseName = segment.data.isDefault ? 'Default' : `Case ${index + 1}`;
		return {
			id: `${visualSourceNodeId}-to-${segment.id}`,
			source: visualSourceNodeId,
			target: segment.id,
			type: EDGE_TYPES.BRANCH,
			data: {
				condition: segment.data.filter,
				caseId: segment.data.caseId,
				caseIndex: index,
				totalCases,
				isDefault: segment.data.isDefault,
				caseName,
				caseColor: segment.data.caseColor,
				switchNodeId: switchNode.id,
			},
		};
	});

	const phantomTargetId = `phantom-target-${switchNode.id}`;
	const phantomTargetNode: Node<BaseNodeData> = {
		id: phantomTargetId,
		type: NODE_TYPES.PHANTOM_TARGET,
		position,
		width: 1,
		height: 1,
		data: {
			label: '',
			category: 'action',
		},
	};

	const phantomEdge: Edge<PhantomAddPathEdgeData> = {
		id: `phantom-add-path-${switchNode.id}`,
		source: visualSourceNodeId,
		sourceHandle: 'default',
		target: phantomTargetId,
		type: EDGE_TYPES.PHANTOM_ADD_PATH,
		data: {
			switchNodeId: switchNode.id,
			segmentCount: totalCases,
		},
	};

	// Position new branch nodes relative to the parent WITHOUT re-laying out
	// the entire graph. Running applyLayout (Dagre) on all nodes would move
	// existing nodes to new positions, causing a jarring visual jump.
	const parentNode = parentNodeId ? nodes.find(n => n.id === parentNodeId) : null;
	const parentX = parentNode?.position.x ?? position.x;
	const parentWidth = parentNode?.measured?.width ?? parentNode?.width ?? SEGMENT_NODE.WIDTH;
	const parentCenterX = parentX + parentWidth / 2;

	// Use the same height logic as the layout engine: prefer the larger of
	// measured and estimated heights for expanded nodes, so the segments
	// appear below the full expanded content (not overlapping it).
	let parentHeight = STEP_NODE_COMPACT_HEIGHT;
	if (parentNode) {
		const measuredH = typeof parentNode.measured?.height === 'number' ? parentNode.measured.height : null;
		const estimatedH = hasEstimatedHeight(parentNode.data) ? parentNode.data.estimatedHeight : null;
		const isExpanded = (parentNode.data as Record<string, unknown>)?.isExpanded === true;

		if (isExpanded) {
			parentHeight =
				measuredH !== null && estimatedH !== null
					? Math.max(measuredH, estimatedH)
					: (measuredH ?? estimatedH ?? STEP_NODE_EXPANDED_MIN_HEIGHT);
		} else {
			parentHeight = measuredH ?? STEP_NODE_COMPACT_HEIGHT;
		}
	}

	const branchSpacing = SEGMENT_NODE.WIDTH + BRANCH_MARGIN;
	const targetY = (parentNode?.position.y ?? position.y) + parentHeight + BRANCH_VERTICAL_GAP;
	const startCenterX = parentCenterX - ((segmentNodes.length - 1) * branchSpacing) / 2;

	for (let i = 0; i < segmentNodes.length; i++) {
		segmentNodes[i] = {
			...segmentNodes[i],
			position: {
				x: startCenterX + i * branchSpacing - SEGMENT_NODE.WIDTH / 2,
				y: targetY,
			},
		};
	}

	// Position hidden switch at the parent position (it's not visible)
	switchNode.position = parentNode?.position ?? position;

	// Position phantom target to the right of the default (last) segment
	const defaultSegment = segmentNodes[segmentNodes.length - 1];
	phantomTargetNode.position = {
		x: defaultSegment.position.x + SEGMENT_NODE.WIDTH + BRANCH_MARGIN,
		y: targetY,
	};

	// Normalize the optimistic seed schedule against the root trigger so that if the user
	// clicks Publish before the canvas reconciles via convertTemplateToFlow, the validator
	// doesn't flag it for being non-anchored under a strictly-anchored root (e.g. Birthday).
	const defaultDoNothingSchedule = normalizeBehaviorBranchSchedule(
		{ criteria: Api.AutomationStepScheduleCriteria.Immediately },
		rootTrigger,
		Api.AutomationStepType.NoAction
	) ?? { criteria: Api.AutomationStepScheduleCriteria.Immediately };
	const defaultDoNothingStep: Api.INoActionAutomationStep = {
		_type: 'NoActionAutomationStep',
		name: 'Do nothing',
		schedule: defaultDoNothingSchedule,
	};
	const defaultDoNothingNode = createStepNodeByType(
		NODE_TYPES.NO_ACTION_STEP,
		{
			x: defaultSegment.position.x + (SEGMENT_NODE.WIDTH - NODE_WIDTH) / 2,
			y: defaultSegment.position.y + SEGMENT_NODE.MIN_HEIGHT + FLOW_VERTICAL_SPACING,
		},
		defaultDoNothingStep,
		automationTemplate
	);
	if (defaultDoNothingNode) {
		defaultDoNothingNode.data.parentCaseInfo = {
			caseIndex: defaultSegment.data.segmentIndex,
			caseName: 'Default',
			isDefault: true,
			caseColor: defaultSegment.data.caseColor,
			parentSwitchNodeId: switchNode.id,
			caseId: defaultSegment.data.caseId,
		};
	}
	const defaultDoNothingEdge: Edge | null = defaultDoNothingNode
		? {
				id: `edge-${defaultSegment.id}-${defaultDoNothingNode.id}`,
				source: defaultSegment.id,
				target: defaultDoNothingNode.id,
				sourceHandle: 'default',
				type: 'default',
			}
		: null;

	const updatedNodes = [
		...nodes,
		switchNode,
		...segmentNodes,
		...(defaultDoNothingNode ? [defaultDoNothingNode] : []),
		phantomTargetNode,
	];
	const updatedEdges = [...edges, ...branchEdges, ...(defaultDoNothingEdge ? [defaultDoNothingEdge] : []), phantomEdge];

	// Re-run switch branch layout so nested conditionals reserve space within
	// existing branch lanes instead of rendering on top of sibling behavior/segment cards.
	const laidOutNodes = applySwitchBranchLayout(updatedNodes, updatedEdges);

	return {
		nodes: laidOutNodes,
		edges: updatedEdges,
		focusNodeId: segmentNodes[0].id,
		switchNodeId: switchNode.id,
	};
}

interface UseCanvasDragDropParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	chainTermini: ChainTerminus[];
	autoConnect: boolean;
	animateEdges: boolean;
	activeContacts: FlowContact[];
	screenToFlowPosition: ReactFlowInstance['screenToFlowPosition'];
	setCenter: ReactFlowInstance['setCenter'];
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	onAddStepToChain?: (
		terminus: ChainTerminus | null,
		stepType: string
	) => Promise<Api.IAutomationStep | null | undefined>;
	/** The automation template - needed for creating new step nodes with proper context */
	automationTemplate?: Api.IAutomationTemplate;
	/** Step node types that are unavailable in the current editor context */
	excludedStepTypes?: string[];
}

interface UseCanvasDragDropReturn {
	closestTerminusId: string | null;
	onDragOver: (event: DragEvent<HTMLDivElement>) => void;
	onDragLeave: () => void;
	onDrop: (event: DragEvent<HTMLDivElement>) => Promise<void>;
}

/**
 * Hook that handles drag-and-drop functionality for adding nodes to the canvas.
 * Extracted from AutomationFlowCanvas to improve maintainability.
 */
export function useCanvasDragDrop({
	nodes,
	edges,
	setNodes,
	setEdges,
	chainTermini,
	autoConnect,
	animateEdges,
	activeContacts,
	screenToFlowPosition,
	setCenter,
	takeSnapshot,
	onAddStepToChain,
	automationTemplate,
	excludedStepTypes,
}: UseCanvasDragDropParams): UseCanvasDragDropReturn {
	const [closestTerminusId, setClosestTerminusId] = useState<string | null>(null);

	const onDragOver = useCallback(
		(event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			event.dataTransfer.dropEffect = 'move';

			const position = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			});
			const closest = findClosestTerminus(position, chainTermini);
			setClosestTerminusId(closest?.nodeId ?? null);
		},
		[screenToFlowPosition, chainTermini]
	);

	const onDragLeave = useCallback(() => {
		setClosestTerminusId(null);
	}, []);

	const onDrop = useCallback(
		async (event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setClosestTerminusId(null);

			const nodeType = event.dataTransfer.getData('application/reactflow');
			if (!nodeType) {
				return;
			}

			if (excludedStepTypes?.includes(nodeType)) {
				toast.error('This step type is not available for this automation.');
				return;
			}

			if (
				nodeType === NODE_TYPES.TRIGGER_AUTOMATION_STEP &&
				!canUseTriggerAutomationStep(automationTemplate?.draft?.trigger ?? automationTemplate?.published?.trigger)
			) {
				toast.error(TRIGGER_AUTOMATION_TRIGGER_TYPE_TOAST);
				return;
			}

			const position = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			});

			const closestTerminus = findClosestTerminus(position, chainTermini);

			const trailingNode = nodes[nodes.length - 1];
			const fallbackTerminus =
				!closestTerminus && autoConnect && trailingNode && canAddStepAfterNode(trailingNode)
					? ({
							nodeId: trailingNode.id,
							node: trailingNode,
							position: { x: 0, y: 0 },
							nestingDepth: 0,
						} as ChainTerminus)
					: null;
			const targetContextTerminus = closestTerminus ?? fallbackTerminus;
			if (targetContextTerminus && isTriggerAutomationStepNode(targetContextTerminus.node)) {
				toast.error(TRIGGER_AUTOMATION_LAST_STEP_TOAST);
				return;
			}

			const targetTemplateId = resolveTargetTemplateIdForTerminus({
				terminus: targetContextTerminus,
				nodes,
				rootTemplateId: automationTemplate?.id,
			});
			const existingTemplate = (
				targetContextTerminus?.node.data as { automationTemplate?: Api.IAutomationTemplate } | undefined
			)?.automationTemplate;
			const nodeTemplate = buildTemplateForNode({
				rootTemplate: automationTemplate,
				targetTemplateId,
				existingTemplate,
			});

			// Handle switch step specially - creates segments
			if (nodeType === NODE_TYPES.SWITCH_STEP) {
				if ((targetContextTerminus?.nestingDepth || 0) >= MAX_BRANCH_NESTING_DEPTH) {
					toast.error(`Conditional nesting is limited to ${MAX_BRANCH_NESTING_DEPTH} levels.`);
					return;
				}

				takeSnapshot('node_add', `Add ${nodeType} step`);

				const parentNodeId = targetContextTerminus?.nodeId;
				const parentNode = parentNodeId ? nodes.find(node => node.id === parentNodeId) : null;
				const basePosition = parentNode?.position ?? position;

				const rootTrigger = automationTemplate?.draft?.trigger ?? automationTemplate?.published?.trigger ?? null;
				const switchResult = addSwitchStepWithSegments(
					nodes,
					edges,
					basePosition,
					parentNodeId,
					nodeTemplate,
					rootTrigger
				);

				const switchEdges = switchResult.edges.map(edge => ({
					...edge,
					animated: animateEdges,
					data: { ...edge.data, activeContacts },
				}));

				setNodes(switchResult.nodes);
				setEdges(switchEdges);

				const focusNode = switchResult.nodes.find(node => node.id === switchResult.focusNodeId);
				if (focusNode) {
					scheduleCenterOnNode(setCenter, focusNode, { duration: 300, zoom: 1 });
				}

				if (onAddStepToChain) {
					const createdStep = await onAddStepToChain(closestTerminus ?? null, nodeType);
					if (createdStep?.id) {
						const persistedSwitchStep = createdStep as Api.ISwitchAutomationStep;
						const caseAssignments = buildPersistedSwitchCaseAssignments({
							nodes: switchResult.nodes,
							optimisticSwitchNodeId: switchResult.switchNodeId,
							persistedSwitchStep,
						});
						const realSwitchNodeId = `step-${createdStep.id}`;

						setNodes(currentNodes =>
							syncOptimisticSwitchNodes({
								nodes: currentNodes,
								optimisticSwitchNodeId: switchResult.switchNodeId,
								persistedSwitchStep,
								automationTemplate: nodeTemplate,
								caseAssignments,
							})
						);
						setEdges(currentEdges =>
							syncOptimisticSwitchEdges({
								edges: currentEdges,
								optimisticSwitchNodeId: switchResult.switchNodeId,
								realSwitchNodeId,
								caseAssignments,
							})
						);
					}
				}
				return;
			}

			// Create node based on type
			const newNode =
				nodeType === NODE_TYPES.TRIGGER
					? createTriggerNode(position)
					: createStepNodeByType(nodeType, position, undefined, nodeTemplate);

			if (!newNode) {
				toast.error(`Unknown node type: ${nodeType}`);
				return;
			}

			if (closestTerminus) {
				const parentCaseInfo = deriveParentCaseInfoForNewNode(closestTerminus, nodes);
				if (parentCaseInfo) {
					newNode.data = {
						...newNode.data,
						parentCaseInfo,
					};
				}
			}

			takeSnapshot('node_add', `Add ${nodeType} step`);

			let result: { nodes: Node<BaseNodeData>[]; edges: Edge[] };

			if (closestTerminus) {
				// All terminus nodes (segments, step nodes) use a source handle with
				// id='default'. The 'case-X' handles only exist on switch step nodes,
				// which are never termini. Pass undefined to use the default handle.
				result = insertNodeAfter(nodes, edges, closestTerminus.nodeId, newNode, undefined);
			} else if (autoConnect && nodes.length > 0 && canAddStepAfterNode(nodes[nodes.length - 1])) {
				const lastNode = nodes[nodes.length - 1];
				const connectToId = wouldCreateCycle(lastNode.id, newNode.id, edges) ? undefined : lastNode.id;
				result = addNodeWithLayout(nodes, edges, newNode, connectToId);
			} else {
				result = addNodeWithLayout(nodes, edges, newNode);
			}

			const updatedEdges = result.edges.map(edge => ({
				...edge,
				animated: animateEdges,
				data: { ...edge.data, activeContacts },
			}));

			setNodes(result.nodes);
			setEdges(updatedEdges);

			const addedNode = result.nodes.find(n => n.id === newNode.id);
			if (addedNode) {
				scheduleCenterOnNode(setCenter, addedNode, { duration: 300, zoom: 1 });
			}

			// Persist to backend if handler provided
			if (onAddStepToChain) {
				const createdStep = await onAddStepToChain(closestTerminus ?? null, nodeType);
				if (createdStep?.id) {
					reconcileOptimisticStepNode({
						optimisticNodeId: newNode.id,
						createdStep,
						automationTemplate: nodeTemplate,
						setNodes,
						setEdges,
					});
				}
			}
		},
		[
			screenToFlowPosition,
			setNodes,
			setEdges,
			nodes,
			edges,
			chainTermini,
			autoConnect,
			animateEdges,
			activeContacts,
			setCenter,
			takeSnapshot,
			onAddStepToChain,
			automationTemplate,
			excludedStepTypes,
		]
	);

	return {
		closestTerminusId,
		onDragOver,
		onDragLeave,
		onDrop,
	};
}
